// src/hooks/charts/useChartQueryDependencies.ts
import { useMemo } from "react";
import { shallowEqual, useSelector } from "react-redux";

import { ForecastMode } from "store/features/app/appSlice";
import { RootState } from "store/rootReducer";
import {
  selectCheckedGlobalForecasts,
  selectCheckedGlobalTypeWells,
  useForecastStore
} from "stores/forecast";

import { EntityKind } from "models/entityKind";

/**
 * Redux selector that extracts only the fields that drive a new API request.
 *
 * Phase 2 of STATE_MANAGEMENT_MIGRATION_PLAN.md — Step 2.1.
 *
 * A change to any field in this selector means the chart must fetch new data
 * from the server. Fields that only affect local visual rendering belong in
 * `selectRenderDeps` (see useChartRenderDependencies.ts).
 */
const selectQueryDeps = (state: RootState, entityKind: EntityKind) => {
  const isWell = entityKind === EntityKind.Well;

  return {
    filterId: isWell ? state.filter.filterId : state.facilityFilter.filterId,
    sortBy: isWell ? state.filter.sortBy : state.facilityFilter.sortBy,
    showGroupsNotInFilter: isWell
      ? state.filter.showGroupsNotInFilter
      : state.facilityFilter.showGroupsNotInFilter,
    currentFilter: isWell
      ? state.filter.currentFilter
      : state.facilityFilter.currentFilter,
    groupBy: isWell ? state.groupBy.globalGroupBy : state.groupBy.globalFacilityFocus,
    hasCheckedBins: state.groupBy.hasCheckedBins,
    hasCheckedBinsFacility: state.groupBy.hasCheckedBinsFacility,
    placeholderBinsEnabled: state.groupBy.placeholderBinsEnabled,
    globalNormalizeBy: state.normalizeBy.globalNormalizeBy,
    useNormalizeBy: state.normalizeBy.useNormalizeBy,
    pdenDataSourceSetting: state.app.pdenDataSourceSetting,
    forecastMode: state.app.forecastMode,
    userForecastSettings: state.app.userForecastSettings,
    useForecastStartDate: state.app.useForecastStartDate,
    syncWells: state.app.syncWells,
    lockedColors: state.app.lockedColors,
    defaultSignificantFigures:
      state.app.significantFiguresSettings.defaultSignificantFigures,
    useStrictRounding: state.app.significantFiguresSettings.useStrictRounding
  };
};

type ReduxQueryDeps = ReturnType<typeof selectQueryDeps>;

export type ChartQueryDependencies = ReduxQueryDeps & {
  checkedGlobalForecasts: ReturnType<typeof selectCheckedGlobalForecasts>;
  checkedGlobalTypeWells: ReturnType<typeof selectCheckedGlobalTypeWells>;
};

/**
 * Hook that subscribes only to query-driving chart dependencies.
 *
 * Use this hook in data-fetching paths (e.g. `useChartDataController`) where
 * a change should trigger a new API request. Components that only need to
 * re-render visually should use `useChartRenderDependencies` instead.
 *
 * Uses `shallowEqual` to avoid unnecessary re-renders when the selector
 * returns a structurally identical object.
 *
 * @param entityKind - Well or Facility; selects the correct filter/groupBy slices.
 */
export const useChartQueryDependencies = (
  entityKind: EntityKind
): ChartQueryDependencies => {
  const reduxDeps = useSelector(
    (state: RootState) => selectQueryDeps(state, entityKind),
    shallowEqual
  );
  const checkedGlobalForecasts = useForecastStore(selectCheckedGlobalForecasts);
  const checkedGlobalTypeWells = useForecastStore(selectCheckedGlobalTypeWells);

  return useMemo(
    () => ({ ...reduxDeps, checkedGlobalForecasts, checkedGlobalTypeWells }),
    [reduxDeps, checkedGlobalForecasts, checkedGlobalTypeWells]
  );
};

/**
 * Default / empty value for query dependencies.
 * Used by test utilities to provide a base mock value.
 */
export const DEFAULT_QUERY_DEPENDENCIES: ChartQueryDependencies = {
  filterId: { id: "", lastUpdated: 0 },
  sortBy: "AlphabeticalAsc",
  showGroupsNotInFilter: false,
  currentFilter: undefined,
  groupBy: undefined,
  hasCheckedBins: undefined,
  hasCheckedBinsFacility: undefined,
  placeholderBinsEnabled: undefined,
  globalNormalizeBy: undefined,
  useNormalizeBy: false,
  pdenDataSourceSetting: undefined,
  forecastMode: ForecastMode.Trump,
  userForecastSettings: undefined,
  useForecastStartDate: false,
  checkedGlobalForecasts: [],
  checkedGlobalTypeWells: [],
  syncWells: undefined,
  lockedColors: {},
  defaultSignificantFigures: undefined,
  useStrictRounding: undefined
};
